"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { Menu, X, ArrowRight, Search } from "lucide-react";
import { siteConfig } from "@/lib/site-config";
import { cn } from "@/lib/utils";
import SearchBox from "./SearchBox";

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ルート遷移時にドロワーを閉じる
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // ドロワー展開中は背面スクロールをロック + Esc で閉じる
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <>
      <header
        className={cn(
          "sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur transition-all duration-300",
          scrolled ? "border-line shadow-[0_1px_12px_rgba(16,32,64,0.06)]" : "border-transparent"
        )}
      >
        <div className="container-content flex h-16 items-center justify-between md:h-20">
          {/* ロゴ（株式会社イケメン） */}
          <Link
            href="/"
            className="group inline-flex items-center transition-opacity hover:opacity-80"
            aria-label={`${siteConfig.companyName} 採用サイト`}
          >
            <Image
              src={siteConfig.logo}
              alt={siteConfig.companyName}
              width={siteConfig.logoWidth}
              height={siteConfig.logoHeight}
              priority
              sizes="(max-width: 768px) 150px, 188px"
              className="h-8 w-auto md:h-10"
            />
          </Link>

          {/* PC ナビ */}
          <nav className="hidden items-center gap-7 lg:flex" aria-label="グローバルナビゲーション">
            {siteConfig.nav.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "group flex flex-col items-center gap-0.5 text-sm font-medium transition-colors",
                  isActive(item.href) ? "text-navy-700" : "text-ink hover:text-navy-700"
                )}
              >
                <span>{item.label}</span>
                <span className="font-en text-[10px] font-semibold uppercase tracking-[0.14em] text-navy-300 group-hover:text-navy-400">
                  {item.labelEn}
                </span>
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            {/* サイト内検索（PC・タブレット） */}
            <SearchBox variant="header" className="hidden md:flex" />

            <Link href={siteConfig.entryUrl} className="btn-primary hidden md:inline-flex">
              <span className="font-en tracking-[0.08em]">ENTRY</span>
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>

            {/* ハンバーガー */}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-navy-900 transition-colors hover:bg-navy-50 active:bg-navy-100 lg:hidden"
              aria-label={open ? "メニューを閉じる" : "メニューを開く"}
              aria-expanded={open}
              aria-controls="mobile-drawer"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      {/*
        モバイルドロワーは header の外（root 直下）に置く。
        理由: sticky な header の中に position:fixed のドロワーを入れると、
        iOS Safari ではドロワーがトップページの hero(isolation:isolate) の下に
        潜り込み、メニューのリンクがタップできない（メニューが「効かない」）。
        root 直下 + z-50 にすることでどのブラウザでも前面に出る。
      */}
      <div
        id="mobile-drawer"
        className={cn(
          "fixed inset-0 top-16 z-50 overflow-hidden lg:hidden",
          open ? "pointer-events-auto" : "pointer-events-none"
        )}
        aria-hidden={!open}
        // 閉じているドロワーは transform で画面外に出しているだけで、
        // リンクはDOMに残っている。inert を付けないとキーボード操作で
        // 見えないメニューの中に入り込める（aria-hidden だけでは防げない）。
        inert={!open}
      >
        <div
          className={cn(
            "absolute inset-0 bg-navy-950/40 transition-opacity duration-300",
            open ? "opacity-100" : "opacity-0"
          )}
          onClick={() => setOpen(false)}
        />
        <nav
          className={cn(
            "absolute right-0 top-0 h-[calc(100vh-4rem)] w-[82%] max-w-sm overflow-y-auto bg-white px-7 py-8 shadow-2xl transition-transform duration-300",
            open ? "translate-x-0" : "translate-x-full"
          )}
          aria-label="モバイルナビゲーション"
        >
          {/* サイト内検索（モバイル） */}
          <Link
            href="/search"
            className="mb-6 flex items-center gap-3 rounded-full border border-line px-4 py-3 text-sm text-muted transition-colors hover:border-navy-300 hover:text-navy-700"
          >
            <Search className="h-4 w-4 shrink-0" aria-hidden />
            記事・求人を検索
          </Link>

          <ul className="flex flex-col divide-y divide-line">
            {siteConfig.nav.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className="flex items-center justify-between py-4 transition-colors hover:text-navy-700"
                >
                  <span className="text-base font-medium text-ink">{item.label}</span>
                  <span className="font-en text-xs font-semibold uppercase tracking-[0.14em] text-navy-300">
                    {item.labelEn}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <Link href={siteConfig.entryUrl} className="btn-primary mt-8 w-full">
            <span className="font-en tracking-[0.08em]">ENTRY</span>
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </nav>
      </div>
    </>
  );
}
